import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageHeader, Section, Field, Input, Textarea } from "@/components/Common";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, Plus, Minus, Trash2, ArrowRight, ShieldCheck, Loader2, CheckCircle2, ArrowLeft, Package } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORIES = ["All", "Apparel", "Tickets", "Cultural", "Other"];

export function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = category === "All" ? products : products.filter(p => p.category === category);

  return (
    <div data-testid="shop-page">
      <PageHeader eyebrow="BCOL Shop" title="Wear the heritage. Support the mission."
        subtitle="Every purchase supports BCOL programs. Branded merchandise, event tickets, and cultural items." />
      <Section>
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`shop-filter-${c.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                category === c ? "bg-[var(--bcol-navy)] text-white" : "bg-white border border-[var(--bcol-border)] text-[var(--bcol-muted)] hover:border-[var(--bcol-navy)]"
              }`}>{c}</button>
          ))}
        </div>

        {loading && <div className="text-center text-[var(--bcol-muted)] py-12">Loading products…</div>}
        {!loading && filtered.length === 0 && (
          <div className="card-soft p-12 text-center max-w-2xl mx-auto" data-testid="shop-empty">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--bcol-navy)] text-[var(--bcol-gold)] flex items-center justify-center mb-5">
              <Package className="w-8 h-8" />
            </div>
            <h3 className="font-display text-2xl text-[var(--bcol-navy)] mb-2">Shop opening soon</h3>
            <p className="text-sm text-[var(--bcol-muted)]">We're curating a collection of BCOL merchandise and cultural items. Check back soon.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {filtered.map((p) => (
            <Link to={`/shop/${p.id}`} key={p.id} className="card-soft overflow-hidden group block" data-testid={`product-${p.id}`}>
              <div className="aspect-square overflow-hidden bg-[var(--bcol-bg)]">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              </div>
              <div className="p-5">
                <div className="text-[10px] tracking-widest uppercase text-[var(--bcol-muted)] mb-1">{p.category}</div>
                <h3 className="font-display text-lg text-[var(--bcol-navy)] mb-1">{p.name}</h3>
                <div className="flex justify-between items-baseline">
                  <span className="font-display text-2xl text-[var(--bcol-navy)]">${p.price.toFixed(2)}</span>
                  <span className="text-xs text-[var(--bcol-gold)] font-semibold">View →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const navigate = useNavigate();

  useEffect(() => { api.get(`/products/${id}`).then(r => setP(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <Section><p className="text-center text-[var(--bcol-muted)]">Product not found. <Link to="/shop" className="underline">Back to shop</Link></p></Section>;
  if (!p) return <Section><div className="h-96 animate-pulse card-soft" /></Section>;

  const addToCart = () => {
    add(p, qty);
    toast.success(`${p.name} added to cart`);
  };

  const buyNow = () => {
    add(p, qty);
    navigate("/cart");
  };

  return (
    <div data-testid="product-detail-page">
      <Section className="bg-white">
        <Link to="/shop" className="text-sm text-[var(--bcol-muted)] hover:text-[var(--bcol-navy)] inline-flex items-center gap-1 mb-7" data-testid="product-back">
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="card-soft overflow-hidden aspect-square">
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="eyebrow mb-3">{p.category}</div>
            <h1 className="font-display text-4xl text-[var(--bcol-navy)] mb-3">{p.name}</h1>
            <div className="font-display text-4xl text-[var(--bcol-gold)] mb-6">${p.price.toFixed(2)}</div>
            <p className="text-[var(--bcol-muted)] leading-relaxed mb-8 whitespace-pre-line">{p.description}</p>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs uppercase tracking-widest text-[var(--bcol-muted)]">Qty</span>
              <div className="inline-flex items-center border border-[var(--bcol-border)] rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:text-[var(--bcol-navy)]" data-testid="qty-decrement"><Minus className="w-4 h-4" /></button>
                <span className="px-4 font-display text-lg" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-2 hover:text-[var(--bcol-navy)]" data-testid="qty-increment"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={addToCart} className="btn-outline" data-testid="add-to-cart"><ShoppingBag className="w-4 h-4" /> Add to cart</button>
              <button onClick={buyNow} className="btn-primary" data-testid="buy-now">Buy now <ArrowRight className="w-4 h-4" /></button>
            </div>
            <div className="mt-7 text-xs text-[var(--bcol-muted)] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[var(--bcol-green)]" /> Secure checkout via Stripe · Free shipping on orders over $75
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export function Cart() {
  const { items, updateQty, remove, subtotal } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal === 0 ? 0 : subtotal >= 75 ? 0 : 8;
  const total = subtotal + shipping;

  return (
    <div data-testid="cart-page">
      <PageHeader eyebrow="Cart" title="Your bag" />
      <Section>
        {items.length === 0 ? (
          <div className="text-center max-w-md mx-auto card-soft p-10" data-testid="cart-empty">
            <ShoppingBag className="w-12 h-12 mx-auto text-[var(--bcol-muted)] mb-4" />
            <h3 className="font-display text-2xl text-[var(--bcol-navy)] mb-2">Your bag is empty</h3>
            <p className="text-sm text-[var(--bcol-muted)] mb-6">Discover BCOL merchandise and cultural items.</p>
            <Link to="/shop" className="btn-primary">Browse shop</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              {items.map((i) => (
                <div key={i.product_id} className="card-soft p-4 flex gap-4 items-center" data-testid={`cart-item-${i.product_id}`}>
                  <img src={i.image_url} alt={i.name} className="w-24 h-24 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="font-display text-lg text-[var(--bcol-navy)]">{i.name}</div>
                    <div className="text-sm text-[var(--bcol-muted)]">${i.price.toFixed(2)}</div>
                    <div className="inline-flex items-center mt-2 border border-[var(--bcol-border)] rounded-full">
                      <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-1.5"><Minus className="w-3 h-3" /></button>
                      <span className="px-3 text-sm">{i.quantity}</span>
                      <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-1.5"><Plus className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg text-[var(--bcol-navy)]">${(i.price * i.quantity).toFixed(2)}</div>
                    <button onClick={() => remove(i.product_id)} className="text-red-600 text-xs mt-2 inline-flex items-center gap-1" data-testid={`cart-remove-${i.product_id}`}>
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <aside>
              <div className="card-soft p-6 sticky top-24">
                <h3 className="font-display text-xl text-[var(--bcol-navy)] mb-5">Order summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-[var(--bcol-muted)]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[var(--bcol-muted)]"><span>Shipping</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
                  {subtotal < 75 && <div className="text-xs text-[var(--bcol-gold)]">Add ${(75 - subtotal).toFixed(2)} more for free shipping</div>}
                  <div className="border-t pt-3 mt-3 flex justify-between font-display text-xl text-[var(--bcol-navy)]"><span>Total</span><span>${total.toFixed(2)}</span></div>
                </div>
                <button onClick={() => navigate("/shop/checkout")} className="btn-primary w-full justify-center mt-6" data-testid="cart-checkout">
                  Checkout <ArrowRight className="w-4 h-4" />
                </button>
                <div className="text-[10px] text-[var(--bcol-muted)] text-center mt-3 flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-[var(--bcol-green)]" /> Secured by Stripe
                </div>
              </div>
            </aside>
          </div>
        )}
      </Section>
    </div>
  );
}

export function ShopCheckout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    customer_name: "", customer_email: "", phone: "",
    shipping_address: "", shipping_city: "", shipping_state: "", shipping_zip: "", shipping_country: "USA",
    notes: "",
  });
  const shipping = subtotal >= 75 ? 0 : 8;
  const total = subtotal + shipping;

  useEffect(() => { if (items.length === 0) navigate("/cart"); }, [items, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...f,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/shop/checkout", payload);
      // Don't clear yet — clear on success page after confirming payment
      sessionStorage.setItem("bcol_pending_order_session", data.session_id);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div data-testid="shop-checkout-page">
      <PageHeader eyebrow="Checkout" title="Shipping & payment" />
      <Section>
        <div className="grid lg:grid-cols-3 gap-10">
          <form onSubmit={submit} className="lg:col-span-2 card-soft p-7 space-y-5" data-testid="checkout-form">
            <h3 className="font-display text-xl text-[var(--bcol-navy)]">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *"><Input required value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="checkout-name" /></Field>
              <Field label="Email *"><Input type="email" required value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} data-testid="checkout-email" /></Field>
              <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="checkout-phone" /></Field>
            </div>
            <h3 className="font-display text-xl text-[var(--bcol-navy)] pt-3">Shipping address</h3>
            <Field label="Street address *"><Input required value={f.shipping_address} onChange={(e) => setF({ ...f, shipping_address: e.target.value })} data-testid="checkout-address" /></Field>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="City *"><Input required value={f.shipping_city} onChange={(e) => setF({ ...f, shipping_city: e.target.value })} data-testid="checkout-city" /></Field>
              <Field label="State *"><Input required value={f.shipping_state} onChange={(e) => setF({ ...f, shipping_state: e.target.value })} data-testid="checkout-state" /></Field>
              <Field label="ZIP *"><Input required value={f.shipping_zip} onChange={(e) => setF({ ...f, shipping_zip: e.target.value })} data-testid="checkout-zip" /></Field>
            </div>
            <Field label="Country"><Input value={f.shipping_country} onChange={(e) => setF({ ...f, shipping_country: e.target.value })} data-testid="checkout-country" /></Field>
            <Field label="Order notes (optional)"><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="checkout-notes" /></Field>
            <button disabled={loading} className="btn-gold w-full justify-center text-base" data-testid="checkout-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Redirecting to Stripe…" : `Pay $${total.toFixed(2)}`}
            </button>
          </form>
          <aside>
            <div className="card-soft p-6 sticky top-24">
              <h3 className="font-display text-xl text-[var(--bcol-navy)] mb-5">Your order</h3>
              <div className="space-y-3 mb-5">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 text-sm">
                    <img src={i.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                    <div className="flex-1">
                      <div className="text-[var(--bcol-navy)]">{i.name}</div>
                      <div className="text-xs text-[var(--bcol-muted)]">Qty {i.quantity}</div>
                    </div>
                    <div className="text-[var(--bcol-navy)]">${(i.price * i.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-[var(--bcol-muted)]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-[var(--bcol-muted)]"><span>Shipping</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
                <div className="border-t pt-3 mt-3 flex justify-between font-display text-xl text-[var(--bcol-navy)]"><span>Total</span><span>${total.toFixed(2)}</span></div>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}

export function ShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking", data: null });

  useEffect(() => {
    if (!sessionId) { navigate("/shop"); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/shop/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ status: "paid", data }); clear(); sessionStorage.removeItem("bcol_pending_order_session"); }
        else if (data.status === "expired") setState({ status: "expired", data });
        else if (attempts >= max) setState({ status: "timeout", data });
        else { attempts++; setTimeout(poll, 2000); }
      } catch {
        if (attempts >= max) setState({ status: "error", data: null });
        else { attempts++; setTimeout(poll, 2000); }
      }
    };
    poll();
  // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div data-testid="shop-success-page">
      <PageHeader eyebrow="Thank you" title="Order received" />
      <Section>
        <div className="max-w-2xl mx-auto card-soft p-10 text-center">
          {state.status === "checking" && (<>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[var(--bcol-navy)]" />
            <p className="mt-4 text-[var(--bcol-muted)]">Confirming your order…</p>
          </>)}
          {state.status === "paid" && (<>
            <div className="w-16 h-16 mx-auto rounded-full bg-[var(--bcol-green)]/15 text-[var(--bcol-green)] flex items-center justify-center"><CheckCircle2 className="w-9 h-9" /></div>
            <h3 className="font-display text-3xl text-[var(--bcol-navy)] mt-5">Order confirmed</h3>
            <p className="text-[var(--bcol-muted)] mt-3">A confirmation has been sent to your inbox. We'll notify you when it ships.</p>
            {state.data?.amount_total && <p className="mt-2 font-semibold text-[var(--bcol-navy)]">Total charged: ${(state.data.amount_total / 100).toFixed(2)}</p>}
            <Link to="/shop" className="btn-primary mt-7">Continue shopping</Link>
          </>)}
          {(state.status === "expired" || state.status === "error") && (<>
            <h3 className="font-display text-2xl text-[var(--bcol-navy)]">Payment didn't complete</h3>
            <p className="text-[var(--bcol-muted)] mt-3">No charge was made. Please try again.</p>
            <Link to="/cart" className="btn-primary mt-6">Back to cart</Link>
          </>)}
          {state.status === "timeout" && (<>
            <h3 className="font-display text-2xl text-[var(--bcol-navy)]">Still processing</h3>
            <p className="text-[var(--bcol-muted)] mt-3">Your order is being processed. Watch your email shortly.</p>
            <Link to="/shop" className="btn-primary mt-6">Continue shopping</Link>
          </>)}
        </div>
      </Section>
    </div>
  );
}
